import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import React, { useEffect, useRef, useState } from 'react';
import isEqual from 'lodash/isEqual';
import { useIntl } from 'react-intl-next';
import { di } from 'react-magnetic-di';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { FlagsProvider, useFlags } from '@atlaskit/flag';
import { ModalTransition } from '@atlaskit/modal-dialog';
import { ContextualAnalyticsData, FireUIAnalytics, SCREEN } from '@atlassian/analytics-bridge';
import { ErrorBoundary } from '@atlassian/proforma-common-core/jira-common';
import { MediaProvider, PfFlagsProvider, PortalCreateContextProvider, UtilsProvider } from '@atlassian/proforma-common-core/jira-common-context';
import { createUtils2 } from '@atlassian/proforma-common-core/jira-common-utils';
import { Action, ActionSubject, AnalyticsProvider } from '@atlassian/proforma-utils/analytics';
import { useUFOComponentExperience } from '@atlassian/ufo';
import { LivePortalCreateFormApi } from '../apis/LivePortalCreateFormApi';
import { portalCreateProductExperience } from '../common/utils/analytics';
import { PackageIntlProvider } from '../intl-provider';
import { PortalCreateStore } from '../stores/PortalCreateStore';
import { PortalCreateObserved } from './PortalCreate';
export var PortalCreateBaseApp = function PortalCreateBaseApp(_ref) {
  var settings = _ref.settings,
    moduleContext = _ref.moduleContext,
    reporterAccountId = _ref.reporterAccountId,
    jiraFieldErrors = _ref.jiraFieldErrors,
    blockFormSubmission = _ref.blockFormSubmission,
    lifecycleHandlers = _ref.lifecycleHandlers;
  var _useAnalyticsEvents = useAnalyticsEvents(),
    createAnalyticsEvent = _useAnalyticsEvents.createAnalyticsEvent;
  var intl = useIntl();
  var _useState = useState(),
    _useState2 = _slicedToArray(_useState, 2),
    portalCreateStore = _useState2[0],
    setPortalCreateStore = _useState2[1];
  var proformaTemplateFormRef = useRef();
  var _useFlags = useFlags(),
    showFlag = _useFlags.showFlag;
  var _settings$context = settings.context,
    registerPortalFormCallback = _settings$context.registerPortalFormCallback,
    onFieldChange = _settings$context.onFieldChange,
    proformaTemplateForm = _settings$context.proformaTemplateForm,
    onFieldDirty = _settings$context.onFieldDirty,
    portalId = _settings$context.portalId;

  // this will update proformaTemplateFormRef only when proformaTemplateForm value has changed
  useEffect(function () {
    if (!isEqual(proformaTemplateForm, proformaTemplateFormRef.current)) {
      proformaTemplateFormRef.current = proformaTemplateForm;
    }
  }, [proformaTemplateForm]);
  useEffect(function () {
    setPortalCreateStore(new PortalCreateStore(intl, moduleContext, registerPortalFormCallback, createAnalyticsEvent, onFieldChange, onFieldDirty, portalId, showFlag, proformaTemplateForm));
    // NOTE: `intl` from `useIntl()` must not be used in a dependency list.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createAnalyticsEvent, moduleContext, registerPortalFormCallback, proformaTemplateFormRef, onFieldChange]);
  return /*#__PURE__*/React.createElement(ErrorBoundary, {
    errorUtils: moduleContext.utils.errorUtils
  }, /*#__PURE__*/React.createElement(PfFlagsProvider, {
    flags: settings.flags
  }, /*#__PURE__*/React.createElement(UtilsProvider, {
    utils: moduleContext.utils
  }, portalCreateStore && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(PortalCreateObserved, {
    portalCreateStore: portalCreateStore,
    reporterAccountId: reporterAccountId,
    jiraFieldErrors: jiraFieldErrors,
    blockFormSubmission: blockFormSubmission,
    lifecycleHandlers: lifecycleHandlers
  }), /*#__PURE__*/React.createElement(FireUIAnalytics, {
    actionSubject: ActionSubject.SECTION,
    action: Action.VIEWED,
    actionSubjectId: "proformaPortalCreate"
  })), /*#__PURE__*/React.createElement(ModalTransition, null, /*#__PURE__*/React.createElement("div", {
    id: "pf-dialog-container"
  })))));
};
export var WrappedPortalCreateApp = function WrappedPortalCreateApp(_ref2) {
  var settings = _ref2.settings,
    reporterAccountId = _ref2.reporterAccountId,
    jiraFieldErrors = _ref2.jiraFieldErrors,
    blockFormSubmission = _ref2.blockFormSubmission,
    lifecycleHandlers = _ref2.lifecycleHandlers;
  var _useFlags2 = useFlags(),
    showFlag = _useFlags2.showFlag;
  var _useAnalyticsEvents2 = useAnalyticsEvents(),
    createAnalyticsEvent = _useAnalyticsEvents2.createAnalyticsEvent;
  var _useState3 = useState(),
    _useState4 = _slicedToArray(_useState3, 2),
    moduleContext = _useState4[0],
    setModuleContext = _useState4[1];
  var _settings$context2 = settings.context,
    intlLocale = _settings$context2.intlLocale,
    projectId = _settings$context2.projectId,
    requestTypeId = _settings$context2.requestTypeId,
    serviceDeskId = _settings$context2.serviceDeskId,
    environment = settings.environment,
    _settings$urls = settings.urls,
    apiUrl = _settings$urls.api,
    jiraUrl = _settings$urls.jira;
  useEffect(function () {
    var utils = createUtils2(jiraUrl, apiUrl, environment, 'PortalCreate', showFlag, createAnalyticsEvent, intlLocale);
    setModuleContext({
      apis: {
        portalCreateFormApi: new LivePortalCreateFormApi(utils.apiUtil, projectId, serviceDeskId, requestTypeId)
      },
      utils: {
        errorUtils: utils.errorUtils
      }
    });
  }, [apiUrl, createAnalyticsEvent, environment, intlLocale, jiraUrl, projectId, requestTypeId, serviceDeskId, showFlag]);
  return moduleContext ? /*#__PURE__*/React.createElement(PortalCreateBaseApp, {
    settings: settings,
    moduleContext: moduleContext,
    reporterAccountId: reporterAccountId,
    jiraFieldErrors: jiraFieldErrors,
    blockFormSubmission: blockFormSubmission,
    lifecycleHandlers: lifecycleHandlers
  }) : /*#__PURE__*/React.createElement(React.Fragment, null);
};

/**
 * When using this component, ensure:
 *  * it is wrapped by an `AnalyticsListener`, if you want to listen to analytics events
 *  * the `settings` property is static and does not change unless necessary (e.g. wrap it in `useState`)
 */
export var PortalCreateApp = function PortalCreateApp(props) {
  useUFOComponentExperience(portalCreateProductExperience);
  var _props$settings$conte = props.settings.context,
    KbSuggestionField = _props$settings$conte.KbSuggestionField,
    intlLocale = _props$settings$conte.intlLocale,
    portalId = _props$settings$conte.portalId;
  var mediaApiUploadToken = props.mediaApiUploadToken,
    mediaApiError = props.mediaApiError;
  var mediaContext = {
    mediaApiUploadToken: mediaApiUploadToken,
    portalId: portalId,
    mediaApiError: mediaApiError
  };
  return /*#__PURE__*/React.createElement(AnalyticsProvider, props.settings.analytics, /*#__PURE__*/React.createElement(ContextualAnalyticsData, {
    sourceName: "proformaJiraPortalCreate",
    sourceType: SCREEN,
    attributes: getAnalyticsAttributes(props.settings.context)
  }, /*#__PURE__*/React.createElement(PackageIntlProvider, {
    locale: intlLocale
  }, /*#__PURE__*/React.createElement(FlagsProvider, null, /*#__PURE__*/React.createElement(MediaProvider, {
    mediaContext: mediaContext
  }, /*#__PURE__*/React.createElement(PortalCreateContextProvider, {
    KbSuggestionField: KbSuggestionField
  }, /*#__PURE__*/React.createElement(WrappedPortalCreateApp, props)))))));
};
var getAnalyticsAttributes = function getAnalyticsAttributes(settings) {
  var serviceDeskId = settings.serviceDeskId,
    requestTypeId = settings.requestTypeId;
  return {
    serviceDeskId: serviceDeskId,
    requestTypeId: requestTypeId,
    projectType: 'service_desk'
  };
};